import { Helmet } from "react-helmet-async";

interface SeoHeadProps {
  title: string;
  description: string;
  path: string;
  ogType?: "website" | "article";
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
  /**
   * Tell search engines not to index this route. Use for authenticated /
   * gated pages so they don't appear in Google with a stale title and
   * return a 401 to the crawler (which hurts domain quality).
   */
  noindex?: boolean;
}

// Canonical production domain. Matches the BASE_URL in
// scripts/generate-sitemap.ts and the Sitemap: directive in robots.txt.
// rel=canonical and og:url MUST be absolute URLs, and they must point to a
// single canonical origin so the multiple serving domains
// (asoftwaretester.app, www.asoftwaretester.app, prepare.asoftwaretester.app,
// preview hosts) consolidate to one instead of fragmenting into duplicate
// content. Never derive this from window.location.origin.
const CANONICAL_BASE_URL = "https://prepare.asoftwaretester.app";

function absoluteUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${CANONICAL_BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}

/**
 * Per-route head tags. Drop one of these into each public page so search
 * engines and JS-executing crawlers (Googlebot) see unique title, description,
 * canonical, and og:* values. Social-only crawlers (LinkedIn, Slack, Facebook)
 * still fall back to the static `<head>` in index.html.
 */
export function SeoHead({ title, description, path, ogType = "website", jsonLd, noindex = false }: SeoHeadProps) {
  const jsonLdItems = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  const url = absoluteUrl(path);
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={ogType} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {noindex ? <meta name="robots" content="noindex,nofollow" /> : null}
      {jsonLdItems.map((item, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(item)}</script>
      ))}
    </Helmet>
  );
}